import { useEffect, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { useSession } from '../../lib/auth.js';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export default function SettingsScreen() {
  const { workspaces } = useSession();
  const ws = workspaces && workspaces[0];
  const [name, setName] = useState('');
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ws) {
      setName(ws.name || '');
      fetch(`${API_BASE}/v1/memberships?workspace_id=${ws.id}`, { credentials: 'include' })
        .then((r) => r.ok ? r.json() : { memberships: [] })
        .then((d) => setMembers(d.memberships || []))
        .catch(() => {});
    }
  }, [ws?.id]);

  const handleRename = async (e) => {
    e.preventDefault();
    if (!ws) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const r = await fetch(`${API_BASE}/v1/workspace/${ws.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data?.error?.message || `HTTP ${r.status}`);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  return (
    <div>
      <h1 style={title}>Settings</h1>

      <section style={section}>
        <h2 style={sectionTitle}>Workspace Name</h2>
        <form onSubmit={handleRename} style={formRow}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={input}
            required
          />
          <button type="submit" disabled={saving} style={saveBtn}>
            {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
            {saved ? 'Saved' : 'Save'}
          </button>
        </form>
        {error && <p style={errText}>{error}</p>}
      </section>

      <section style={section}>
        <h2 style={sectionTitle}>Members</h2>
        {members.length === 0 ? (
          <p style={muted}>No members loaded.</p>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>User ID</th>
                <th style={th}>Role</th>
                <th style={th}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td style={td}>{m.user_id?.slice(0, 12) || m.user_id}</td>
                  <td style={td}>{m.role}</td>
                  <td style={td}>{new Date(m.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p style={muted}>Invite by email  -  coming soon.</p>
      </section>
    </div>
  );
}

const title = { fontSize: 'var(--fs-22)', fontWeight: 600, color: 'var(--fg-0)', marginBottom: 'var(--space-5)' };
const section = { marginBottom: 'var(--space-6)' };
const sectionTitle = { fontSize: 'var(--fs-16)', fontWeight: 500, color: 'var(--fg-0)', marginBottom: 'var(--space-3)', marginTop: 0 };
const formRow = { display: 'flex', gap: 'var(--space-2)', alignItems: 'center' };
const input = { flex: 1, padding: 'var(--space-2) var(--space-3)', background: 'var(--bg-3)', border: '1px solid var(--line-2)', borderRadius: 'var(--r-1)', color: 'var(--fg-0)', fontSize: 'var(--fs-14)' };
const saveBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: 'var(--space-2) var(--space-4)', background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none', borderRadius: 'var(--r-1)', fontSize: 'var(--fs-13)', cursor: 'pointer' };
const errText = { color: 'var(--danger)', fontSize: 'var(--fs-12)', marginTop: 'var(--space-2)' };
const muted = { color: 'var(--fg-3)', fontSize: 'var(--fs-13)', marginTop: 'var(--space-2)' };
const table = { width: '100%', borderCollapse: 'collapse' };
const th = { textAlign: 'left', fontSize: 'var(--fs-11)', color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)', padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--line-1)' };
const td = { padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--fs-13)', color: 'var(--fg-1)', borderBottom: '1px solid var(--line-1)' };
